import React, { useMemo } from 'react';
import { filter, sortBy } from 'lodash-es';
import Plot from 'react-plotly.js';
import { PlotData } from 'plotly.js';
import { useTranslation } from 'react-i18next';

import { Station } from '../../types';
import Icon from '../icon';
import { parseMetrics } from '../../utils/data.utils';
import styles from './styles.module.scss';

interface Props {
  station: Station;
  onClose: () => void;
}

interface ValueMetric {
  id: number;
  name: string;
}

export const ForecastsChart = (props: Props) => {
  const { station } = props;
  const { t } = useTranslation();
  const data = station.values;

  const traces: Partial<PlotData>[] = useMemo(() => {
    const dataWithValues = data.filter(d => d.value !== null);
    const metrics = parseMetrics(dataWithValues);

    const traces = metrics.map(metric => {
      const dataByMetric = sortBy(
        filter(dataWithValues, {
          metricId: metric.id,
        }),
        'datetimeStart'
      );
      const plotData: Partial<PlotData> = {
        x: dataByMetric.map(d => new Date(d.datetimeStart * 1000)),
        y: dataByMetric.map(dataItem => {
          if (dataItem.value !== dataItem.missingValue) return dataItem.value;
          return null;
        }),
        name: metric.name,
      };

      return plotData;
    });

    return traces;
  }, [data]);

  // render the component
  return render();

  function render() {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <span>
            {t('stationPopup.station')}: {station.localCode}
          </span>
          <div className={styles.buttonClose} onClick={props.onClose}>
            <Icon name="times" fill="#A3A3A3" size={11} />
          </div>
        </div>

        <div className={styles.content}>
          <div className={styles.chart}>
            <Plot
              style={{ width: '100%', height: '100%' }}
              data={traces}
              layout={{
                autosize: true,
                xaxis: { automargin: true, zeroline: false },
                yaxis: {
                  automargin: true,
                  zeroline: false,
                  tickformat: '.1f',
                  ticksuffix: ' µg/m³',
                },
                showlegend: true,
                margin: { t: 40, b: 0, l: 0, r: 0 },
              }}
            />
          </div>
        </div>
      </div>
    );
  }
};
